import { service_open_pose } from '@/services/open_pose/service_open_pose';
import * as THREE from 'three';
import { ConstantsOpenPose } from '../constants_open_pose';
import { OpenPoseCameraBounds } from '../open_pose_types/open_pose_camera_bounds';
import { OpenPoseFirstPersonControls } from './open_pose_first_person_controls';

export class OpenPoseCameraController {
  /*
   * FIELDS
   */

  is_camera_perspective: boolean;

  camera_perspective: THREE.PerspectiveCamera;
  camera_orthographic: THREE.OrthographicCamera;

  private first_person_controls: OpenPoseFirstPersonControls;

  private last_time_ms: number;

  private last_valid_position: THREE.Vector3;
  private last_valid_quaternion: THREE.Quaternion;

  private is_wasd_enabled: boolean;

  bounds: OpenPoseCameraBounds;

  private get renderer() {
    return service_open_pose.renderer;
  }

  /*
   * METHODS
   */

  constructor() {
    this.is_camera_perspective = true;

    this.last_time_ms = 0;

    this.last_valid_position = new THREE.Vector3();
    this.last_valid_quaternion = new THREE.Quaternion();

    this.is_wasd_enabled = false;

    this.bounds = ConstantsOpenPose.CAMERA_BOUNDS;

    const aspect = this.get_aspect_ratio();
    this.camera_perspective = new THREE.PerspectiveCamera(60, aspect, 0.1, 10000);
    this.camera_perspective.position.set(0, 100, 200);
    this.camera_perspective.lookAt(0, 100, 0);
    this.camera_perspective.updateProjectionMatrix();

    const frustum_height = this.calculate_orthographic_size();
    const frustum_width = frustum_height * aspect;

    this.camera_orthographic = new THREE.OrthographicCamera(
      -frustum_width / 2,
      frustum_width / 2,
      frustum_height - frustum_height / 4,
      -frustum_height / 4,
      0.1,
      10000,
    );

    this.camera_orthographic.position.set(0, 100, 1000);
    this.camera_orthographic.up.set(0, 1, 0);
    this.camera_orthographic.lookAt(0, 100, 0);
    this.camera_orthographic.updateProjectionMatrix();

    this.first_person_controls = new OpenPoseFirstPersonControls(this.camera_perspective);
  }

  init() {
    service_open_pose.set_camera(this.camera_perspective);
  }

  private get_aspect_ratio() {
    const canvas = this.renderer.domElement;

    return Math.max(1e-6, canvas.clientWidth / Math.max(1, canvas.clientHeight));
  }

  calculate_orthographic_size() {
    const canvas = this.renderer.domElement;
    const canvas_aspect = Math.max(1e-6, canvas.clientWidth / Math.max(1, canvas.clientHeight));

    const billboard_aspect = service_open_pose.billboard_controller.width / Math.max(1e-6, service_open_pose.billboard_controller.height);

    const out = canvas_aspect > billboard_aspect ? service_open_pose.billboard_controller.height : service_open_pose.billboard_controller.width / canvas_aspect;

    return out;
  }

  update_camera_projection() {
    const aspect = this.get_aspect_ratio();

    if (this.is_camera_perspective) {
      this.camera_perspective.aspect = aspect;
      this.camera_perspective.updateProjectionMatrix();
      return;
    }

    const frustum_height = this.calculate_orthographic_size();
    const frustum_width = frustum_height * aspect;

    this.camera_orthographic.left = -frustum_width / 2;
    this.camera_orthographic.right = frustum_width / 2;
    this.camera_orthographic.top = frustum_height - frustum_height / 4;
    this.camera_orthographic.bottom = -frustum_height / 4;
    this.camera_orthographic.updateProjectionMatrix();
  }

  get_camera_current(): THREE.Camera {
    return this.is_camera_perspective ? this.camera_perspective : this.camera_orthographic;
  }

  toggle_camera() {
    this.is_camera_perspective = !this.is_camera_perspective;

    if (this.is_camera_perspective) {
      this.disable_first_person_control();

      const camera = this.camera_perspective;

      camera.up.set(0, 1, 0);
      camera.updateProjectionMatrix();

      service_open_pose.set_camera(camera);
    } else {
      this.disable_first_person_control();

      const camera = this.camera_orthographic;

      camera.position.set(0, 100, 1000);
      camera.up.set(0, 1, 0);
      camera.lookAt(0, 100, 0);
      camera.updateProjectionMatrix();

      service_open_pose.set_camera(camera);
    }

    this.update_camera_projection();
  }

  reset_perspective_in_front_of(body_world_position: THREE.Vector3) {
    const camera = this.camera_perspective;

    const distance_z = Math.max(1, ConstantsOpenPose.CAMERA_INIT_DISTANCE_BLOCKS) * ConstantsOpenPose.SCENE_GRID_BLOCK_SIZE;
    const distance_y = Math.max(0, ConstantsOpenPose.CAMERA_INIT_HEIGHT_OFFSET_BLOCKS) * ConstantsOpenPose.SCENE_GRID_BLOCK_SIZE;

    camera.position.set(body_world_position.x, body_world_position.y + distance_y, body_world_position.z + distance_z);
    camera.lookAt(body_world_position.x, body_world_position.y + distance_y, body_world_position.z);
    camera.updateProjectionMatrix();

    camera.position.x = Math.max(this.bounds.x.min, Math.min(this.bounds.x.max, camera.position.x));
    camera.position.y = Math.max(this.bounds.y.min, Math.min(this.bounds.y.max, camera.position.y));
    camera.position.z = Math.max(this.bounds.z.min, Math.min(this.bounds.z.max, camera.position.z));
  }

  enable_first_person_control() {
    if (!this.is_camera_perspective) {
      this.is_wasd_enabled = false;
      return;
    }

    const camera_object = this.camera_perspective as unknown as THREE.Object3D;

    this.first_person_controls.movement_speed = ConstantsOpenPose.CAMERA_WASD_MOVEMENT_SPEED_UNITS_PER_SECOND;
    this.first_person_controls.look_speed_x = ConstantsOpenPose.CAMERA_WASD_LOOK_SPEED_X;
    this.first_person_controls.look_speed_y = ConstantsOpenPose.CAMERA_WASD_LOOK_SPEED_Y;

    this.first_person_controls.min_y = this.bounds.y.min;
    this.first_person_controls.max_y = this.bounds.y.max;

    const first_person_controls_object = this.first_person_controls.get_object();
    this.first_person_controls.add_listeners();
    first_person_controls_object.position.copy((camera_object as any).position);

    const forward_vector = new THREE.Vector3(0, 0, -1).applyQuaternion((camera_object as any).quaternion);
    forward_vector.y = 0;

    if (forward_vector.lengthSq() > 1e-8) forward_vector.normalize();

    const yaw_rotation_y = Math.atan2(forward_vector.x, -forward_vector.z);

    this.first_person_controls.yaw.rotation.set(0, yaw_rotation_y, 0);
    this.first_person_controls.pitch.rotation.set(0, 0, 0);

    camera_object.position.set(0, 0, 0);
    camera_object.quaternion.set(0, 0, 0, 1);
    camera_object.updateMatrixWorld(true);

    service_open_pose.scene.add(first_person_controls_object);

    this.last_valid_position.copy(first_person_controls_object.position);
    this.last_valid_quaternion.copy(first_person_controls_object.quaternion);

    this.is_wasd_enabled = true;
  }

  public disable_first_person_control() {
    this.is_wasd_enabled = false;

    const controls = this.first_person_controls;
    const camera_object = this.camera_perspective as unknown as THREE.Object3D;

    controls.pitch.remove(camera_object);

    service_open_pose.scene.remove(controls.get_object());

    controls.dispose();
  }

  private clamp_fp_object_to_bounds_and_report() {
    const position = this.first_person_controls.get_object().position;
    const bounds = this.bounds;

    let clamped_x = false;
    let clamped_y = false;
    let clamped_z = false;

    if (position.x < bounds.x.min) {
      position.x = bounds.x.min;
      clamped_x = true;
    } else if (position.x > bounds.x.max) {
      position.x = bounds.x.max;
      clamped_x = true;
    }

    if (position.y < bounds.y.min) {
      position.y = bounds.y.min;
      clamped_y = true;
    } else if (position.y > bounds.y.max) {
      position.y = bounds.y.max;
      clamped_y = true;
    }

    if (position.z < bounds.z.min) {
      position.z = bounds.z.min;
      clamped_z = true;
    } else if (position.z > bounds.z.max) {
      position.z = bounds.z.max;
      clamped_z = true;
    }

    return { x: clamped_x, y: clamped_y, z: clamped_z };
  }

  update_first_person_control(delta_time_milliseconds: number) {
    if (!this.is_wasd_enabled) return;

    const delta_seconds_raw = Math.max(0, delta_time_milliseconds) / 1000;
    const delta_seconds = Math.min(delta_seconds_raw, 0.05);

    const controls = this.first_person_controls;
    const object_3d = controls.get_object();

    const previous_x = object_3d.position.x;
    const previous_y = object_3d.position.y;
    const previous_z = object_3d.position.z;

    controls.update(delta_seconds);

    const clamped_flags = this.clamp_fp_object_to_bounds_and_report();
    this.zero_velocity_if_available(clamped_flags);

    const position = object_3d.position;

    if (!Number.isFinite(position.x) || !Number.isFinite(position.y) || !Number.isFinite(position.z)) {
      object_3d.position.copy(this.last_valid_position);
      object_3d.quaternion.copy(this.last_valid_quaternion);
    } else if (position.x !== previous_x || position.y !== previous_y || position.z !== previous_z) {
      this.last_valid_position.copy(position);
      this.last_valid_quaternion.copy(object_3d.quaternion);
    }
  }

  rotation_by_mouse_enable() {
    this.first_person_controls.set_is_rotation_by_mouse_enabled(true);
  }

  rotation_by_mouse_disable() {
    this.first_person_controls.set_is_rotation_by_mouse_enabled(false);
  }

  tick(time_ms: number) {
    const last = this.last_time_ms;

    const delta = last === 0 ? 0 : time_ms - last;

    this.last_time_ms = time_ms;

    this.update_first_person_control(delta);
  }

  set_camera_into_scene_current() {
    service_open_pose.set_camera(this.get_camera_current());
  }

  dispose() {
    this.first_person_controls.dispose();
  }

  private zero_velocity_if_available(_: { x: boolean; y: boolean; z: boolean }) {}
}
